/**
 * The training match is a real, seeded sim — so the producer can "know" what's
 * coming by running a shadow copy of the same sim ahead of time. Same seed →
 * same match, regardless of what the player does on the console.
 */
import type { MatchConfig, MatchEventType } from '../core/contracts';
import { createFootballSim } from '../sim/football';

export interface TrainingTimeline {
  /** First real shot (sim seconds) and where it was struck. */
  shotT: number;
  shotX: number;
  /** Ball goes dead after that shot (stoppage / set piece). */
  stopT: number;
  /** First calm open play after the stoppage — graphics + booth lessons go here. */
  calmT: number;
  /** First goal (Infinity if the seed has none before half time). */
  goalT: number;
  goalX: number;
  /** When the producer starts warning that the goal move is building. */
  buildT: number;
  /** A big chance (woodwork / save / near miss) between buildT and the goal, if any. */
  chanceT: number | null;
  chanceType: MatchEventType | null;
}

const SHOTS = new Set<MatchEventType>(['shot', 'shot_on_target']);
const CHANCES = new Set<MatchEventType>(['woodwork', 'save', 'near_miss']);
const DEAD = new Set(['stoppage', 'set_piece', 'goal_celebration', 'half_time']);
const STEP = 1 / 30;

export function scanTimeline(config: MatchConfig): TrainingTimeline {
  const sim = createFootballSim(config);
  const tl: TrainingTimeline = {
    shotT: Infinity, shotX: 0, stopT: Infinity, calmT: Infinity, goalT: Infinity, goalX: 0,
    buildT: Infinity, chanceT: null, chanceType: null,
  };
  const chances: { t: number; type: MatchEventType }[] = [];
  const limit = config.halfLengthSeconds;
  let t = 0;
  while (!sim.finished && t < limit) {
    const evs = sim.step(STEP);
    const s = sim.snapshot();
    t = s.t;
    for (const e of evs) {
      if (tl.shotT === Infinity && e.t > 6 && SHOTS.has(e.type)) { tl.shotT = e.t; tl.shotX = e.pos.x; }
      if (CHANCES.has(e.type)) chances.push({ t: e.t, type: e.type });
      if (e.type === 'goal' && tl.goalT === Infinity) { tl.goalT = e.t; tl.goalX = e.pos.x; }
    }
    if (tl.shotT < Infinity && tl.stopT === Infinity && t > tl.shotT && DEAD.has(s.phase)) tl.stopT = t;
    if (tl.stopT < Infinity && tl.calmT === Infinity && t > tl.stopT + 1 && s.phase === 'open_play' && s.intensity < 0.3) tl.calmT = t;
    if (tl.goalT < Infinity && t > tl.goalT + 1) break;
  }
  if (tl.goalT < Infinity) {
    tl.buildT = Math.max(tl.calmT < Infinity ? tl.calmT + 6 : 0, tl.goalT - 5.5);
    const c = chances.find((x) => x.t > tl.buildT && x.t < tl.goalT - 0.4);
    if (c) { tl.chanceT = c.t; tl.chanceType = c.type; tl.buildT = Math.min(tl.buildT, c.t - 2.5); }
  }
  return tl;
}
